import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, TrendingUp, Activity, BarChart3, Volume2 } from 'lucide-react';

interface ScreeningResult {
  id: string;
  token_symbol: string;
  token_name: string;
  current_price: number;
  composite_score: number;
  ema_score: number | null;
  rsi_value: number | null;
  rsi_score: number | null;
  macd_score: number | null;
  macd_crossover_signal: string | null;
  volume_increase_pct: number | null;
  volume_score: number | null;
  trend_strength: string | null;
}

function ScoreGauge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-success' : score >= 60 ? 'text-warning' : 'text-muted-foreground';
  const bgColor = score >= 80 ? 'stroke-success/20' : score >= 60 ? 'stroke-warning/20' : 'stroke-muted/20';
  const fgColor = score >= 80 ? 'stroke-success' : score >= 60 ? 'stroke-warning' : 'stroke-muted-foreground';
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="36" fill="none" strokeWidth="6" className={bgColor} />
        <circle cx="40" cy="40" r="36" fill="none" strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset} className={fgColor} />
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${color}`}>
        <span className="text-lg font-bold">{score}</span>
        <span className="text-[10px] opacity-70">/ 100</span>
      </div>
    </div>
  );
}

function formatMacdSignal(signal: string | null): string {
  if (!signal || signal === 'none') return 'No crossover';
  if (signal.includes('0d')) return 'Crossed today';
  if (signal.includes('1d')) return 'Crossed yesterday';
  if (signal.includes('2d')) return 'Crossed 2d ago';
  return signal;
}

function formatPrice(price: number): string {
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function OpportunityCard({ result, rank, livePrice }: { result: ScreeningResult; rank: number; livePrice?: number }) {
  const tradeUrl = `https://crypto.com/exchange/trade/${result.token_symbol}_USDT`;
  const displayPrice = livePrice ?? result.current_price;
  const isLive = livePrice != null;

  // P&L from screening entry price to live price
  const pnl = isLive && result.current_price > 0
    ? ((livePrice - result.current_price) / result.current_price) * 100
    : null;

  return (
    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <ScoreGauge score={result.composite_score} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-muted-foreground font-mono">#{rank}</span>
              <h3 className="font-bold text-lg truncate">{result.token_symbol}</h3>
              <span className="text-sm text-muted-foreground truncate">{result.token_name}</span>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl font-semibold">{formatPrice(displayPrice)}</span>
              {isLive && (
                <Badge className="bg-success/20 text-success border-success/30 text-[10px]" variant="outline">
                  Live
                </Badge>
              )}
              {pnl !== null && (
                <span className={`text-sm font-semibold ${pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}%
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-primary" />
                <span className="text-muted-foreground">Trend:</span>
                <span className="font-medium capitalize">
                  {result.trend_strength === 'golden_cross' ? '🔥 Golden Cross' : result.trend_strength === 'above_both' ? 'Bullish' : 'Weak'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-primary" />
                <span className="text-muted-foreground">MACD:</span>
                <span className="font-medium">{formatMacdSignal(result.macd_crossover_signal)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-primary" />
                <span className="text-muted-foreground">RSI:</span>
                <span className="font-medium">{result.rsi_value?.toFixed(1) ?? 'N/A'}</span>
                {result.rsi_value && (
                  <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden max-w-[50px]">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, result.rsi_value)}%` }} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <Volume2 className="w-3.5 h-3.5 text-primary" />
                <span className="text-muted-foreground">Vol:</span>
                <span className="font-medium text-success">+{(result.volume_increase_pct ?? 0).toFixed(0)}%</span>
              </div>
            </div>
          </div>
        </div>

        <a href={tradeUrl} target="_blank" rel="noopener noreferrer" className="block mt-4">
          <Button className="w-full bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30" variant="outline" size="sm">
            Trade on Crypto.com <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </Button>
        </a>
      </CardContent>
    </Card>
  );
}
